import * as Sentry from '@sentry/react';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

// Components
import { ProductDetails } from '@/components/ProductDetails/ProductDetails';
import { Spinner } from '@/components/Spinner/Spinner';
import { Context } from '@/components/Store';
import { ActionTypes } from '@/components/Store.d';
import { Supporter } from '@/components/Supporter/Supporter';
import { VoucherInput } from '@/components/VoucherInput/VoucherInput';
import Auth from '@/lib/authentication';
// Modules
import { config } from '@/lib/config';
import { dst } from '@/lib/events';
import { useMessage, useNavigate } from '@/lib/hooks';
import { authenticate, checkMoveSubscription, getBundle } from '@/lib/http';
import { error as errorLog, log } from '@/lib/log';
import { getPricingText, getProductClass, isSupporter, setTitle } from '@/lib/util';

const Product = () => {
	const [state, dispatch] = useContext(Context);
	const [isLoading, setIsLoading] = useState(true);
	const [didClickNext, setDidClickNext] = useState(false);
	const [showActiveProductWarning, setShowActiveProductWarning] = useState(false);
	const [isEligibilityChecked, setIsEligibilityChecked] = useState(false);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	// Initial setup
	useEffect(() => {
		dispatch({
			type: ActionTypes.setEntryPage,
			payload: {
				key: 'entryPage',
				value: window.location.href
			}
		});
	}, [dispatch]);

	// Load bundle data
	useEffect(() => {
		void (async () => {
			let bundleData;

			try {
				bundleData = await getBundle();
			} catch (err) {
				errorLog(err);

				log(`Change route to ${config.routes.error}?code=404`);
				navigate(config.routes.error, { searchParams: { code: '404' } });
			}

			// add tag with productID for Sentry
			if (bundleData?.bundleReference) {
				Sentry.setTag('productId', bundleData.bundleReference);
			}

			dispatch({
				type: ActionTypes.bundleData,
				payload: {
					key: 'bundle',
					value: bundleData
				}
			});

			dispatch({
				type: ActionTypes.setProductClass,
				payload: getProductClass(bundleData)
			});

			setTitle(bundleData?.name);
			setIsLoading(false);
		})().catch((err) => {
			errorLog('Unexpected error in bundle loading:', err);
		});
	}, [dispatch, navigate]);

	// Is upgradable?
	useEffect(() => {
		void (async () => {
			const voucher = state?.voucher ?? undefined;

			if (state?.bundle && state.bundle.contractReference && (await Auth.userIsLoggedIn())) {
				try {
					const response = await checkMoveSubscription(state.bundle.contractReference);

					if (response?.alreadyPurchased) {
						setShowActiveProductWarning(true);
						setIsEligibilityChecked(true);
						return;
					}

					// The trial price is only for users who never had a subscription in the product group.
					// If the backend couldn't tell, play it safe and don't show the trial price.
					const eligibleForTrialPrice = response?.eligibleForTrial ?? false;

					dispatch({
						type: ActionTypes.setEligibleForTrial,
						payload: eligibleForTrialPrice
					});

					if (response?.subscriptionOnHoliday) {
						dispatch({
							type: ActionTypes.setMovableSubscription,
							payload: response
						});

						dispatch({
							type: ActionTypes.setPricingText,
							payload: !isSupporter(state.bundle)
								? getPricingText(state.bundle, eligibleForTrialPrice, voucher)
								: ''
						});

						setIsEligibilityChecked(true);
						return;
					}

					dispatch({
						type: ActionTypes.setMovableSubscription,
						payload: response
					});

					dispatch({
						type: ActionTypes.setPricingText,
						payload: !isSupporter(state.bundle)
							? getPricingText(state.bundle, eligibleForTrialPrice, voucher)
							: ''
					});

					setIsEligibilityChecked(true);
				} catch (e) {
					log({ e });
				}
			} else {
				dispatch({
					type: ActionTypes.setPricingText,
					payload:
						state.bundle && !isSupporter(state.bundle)
							? getPricingText(state.bundle, true, voucher)
							: ''
				});
				// No checkMoveSubscription needed (not logged in or no contractRef) - safe to proceed
				setIsEligibilityChecked(true);
			}
		})().catch((error) => {
			errorLog('Unexpected error in upgrade eligibility check:', error);
		});
	}, [dispatch, navigate, state.bundle, state.voucher]);

	useEffect(() => {
		Auth.onSignedIn(async (session) => {
			log("Callback 'onSignedIn' has been triggered", session);

			if (!session?.idToken?.jwtToken) {
				errorLog('No JWT token in session');
				return;
			}

			let payload;

			try {
				payload = await authenticate(session.idToken.jwtToken);
			} catch (err) {
				errorLog(
					`Authentication failed: ${err instanceof Error ? err.message : JSON.stringify(err)}`
				);

				if (err instanceof Error && err.message.includes('The email is already in use.')) {
					navigate(config.routes.error, { searchParams: { code: 'emailInUse' } });
				} else {
					navigate(config.routes.error);
				}

				return;
			}

			dispatch({
				type: ActionTypes.setMppCredentials,
				payload: payload
			});

			dispatch({
				type: ActionTypes.setTokenId,
				payload: {
					key: 'user.tokenID',
					value: session.idToken.jwtToken
				}
			});

			if (didClickNext) {
				const target = state?.bundle?.containsPhysical
					? config.routes.address
					: config.routes.payment;

				setDidClickNext(false);

				log('Change route to', target);
				navigate(target);
			}
		});
	}, [isEligibilityChecked, navigate, state?.bundle, state?.upgrade, didClickNext, dispatch]);

	// Makes sure that the printAccountNumber is read from the URL and set in sessionData
	useEffect(() => {
		if (!searchParams.has('pan')) {
			return;
		}

		const printAccountNumber = searchParams.get('pan');

		dispatch({
			type: ActionTypes.printAccountNumberChange,
			payload: {
				key: 'printAccountNumber',
				value: printAccountNumber
			}
		});
	}, [dispatch, searchParams]);

	useMessage((event: MessageEvent) => {
		if (event.origin !== window.location.origin || event.data.eventName !== dst.auth.stateChanged) {
			return;
		}

		window.location.reload();
	});

	return (
		<>
			<VoucherInput hidden={true} />
			{isLoading ? (
				<Spinner />
			) : isSupporter(state.bundle) ? (
				<Supporter
					isEligibilityChecked={isEligibilityChecked}
					setDidClickNext={setDidClickNext}
					showActiveProductWarning={showActiveProductWarning}
				/>
			) : (
				<ProductDetails
					isEligibilityChecked={isEligibilityChecked}
					setDidClickNext={setDidClickNext}
					showActiveProductWarning={showActiveProductWarning}
				/>
			)}
		</>
	);
};

export { Product };
